// SELECT-list projection-alias extractor for the IntelliSense Engine.
// Used by HAVING / ORDER-BY (and engine-specific GROUP-BY in MySQL/PG/SQLite)
// positions where a SELECT-list alias (`SELECT … AS foo`) is a legal
// reference. WHERE and JOIN-ON are intentionally excluded — aliases aren't
// computed yet at that point in the SQL evaluation order.
//
// Strategy:
//   1. Tokenize the statement region once.
//   2. Find the most recent depth-0 `SELECT` keyword BEFORE the cursor — that
//      is the SELECT whose projection aliases are in scope.
//   3. Walk forward to the first depth-0 clause-boundary keyword (FROM /
//      WHERE / GROUP / HAVING / ORDER / LIMIT / OFFSET / FETCH / INTO /
//      UNION / INTERSECT / EXCEPT) — that bounds the select list.
//   4. Inside the select list, split projections by depth-0 commas, and for
//      each item extract the alias bound via the last depth-0 `AS <ident>`.
//
// Bare-form aliases (`SELECT a b FROM t`) are NOT extracted — only explicit
// `AS`-bound aliases — to avoid mis-identifying trailing keywords (`SELECT a
// FOR UPDATE`) as aliases.

import type {
  CompletionCandidate,
  CompletionPositionKind,
  DatabaseEngineId,
  OffsetRange,
} from '../../../core/engines';
import { tokenize, type Token } from '../context/tokenizer';

import { parseIdentifierToken } from './identifierUtils';

export type SelectListAlias = {
  readonly name: string;
  readonly start: number;
  readonly end: number;
};

const SELECT_LIST_TERMINATORS: ReadonlySet<string> = new Set([
  'FROM',
  'WHERE',
  'GROUP',
  'HAVING',
  'ORDER',
  'LIMIT',
  'OFFSET',
  'FETCH',
  'INTO',
  'UNION',
  'INTERSECT',
  'EXCEPT',
]);

const SET_OPERATION_KEYWORDS: ReadonlySet<string> = new Set(['UNION', 'INTERSECT', 'EXCEPT']);

export function extractSelectListAliases(
  source: string,
  statementRange: OffsetRange,
  cursor: number,
  engineId: DatabaseEngineId,
): SelectListAlias[] {
  const tokens = tokenize(source, statementRange.start, statementRange.end, engineId);
  if (tokens.length === 0) return [];

  // Collect every depth-0 SELECT before the cursor, and note whether a depth-0
  // set-operation keyword (UNION / INTERSECT / EXCEPT) precedes the cursor.
  let depth = 0;
  const selectIdxs: number[] = [];
  let hasSetOpBeforeCursor = false;
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i]!;
    if (t.start > cursor) break;
    if (t.type === 'lparen') {
      depth++;
      continue;
    }
    if (t.type === 'rparen') {
      depth = Math.max(0, depth - 1);
      continue;
    }
    if (depth !== 0) continue;
    if (t.type === 'kw') {
      const up = t.text.toUpperCase();
      if (up === 'SELECT') selectIdxs.push(i);
      else if (SET_OPERATION_KEYWORDS.has(up)) hasSetOpBeforeCursor = true;
    }
  }
  if (selectIdxs.length === 0) return [];

  // Normally only the SELECT immediately enclosing the cursor contributes
  // aliases. In a set-operation compound (… UNION/INTERSECT/EXCEPT …) the
  // compound's output column names are derived from the branches (SQL standard:
  // the first query's names) and are referenceable at the compound's ORDER BY,
  // so harvest every depth-0 branch and union their aliases. (Oracle `MINUS` is
  // not tokenized as a keyword, so a MINUS compound is not treated as a set-op
  // boundary here — none of the covered checkpoints rely on it.)
  const branchStarts = hasSetOpBeforeCursor
    ? selectIdxs
    : [selectIdxs[selectIdxs.length - 1]!];

  const aliases: SelectListAlias[] = [];
  const seen = new Set<string>();
  for (const selectIdx of branchStarts) {
    for (const alias of collectBranchAliases(tokens, selectIdx)) {
      if (seen.has(alias.name)) continue;
      seen.add(alias.name);
      aliases.push(alias);
    }
  }
  return aliases;
}

// Extract the AS-bound projection aliases of a single SELECT branch, bounded by
// the next depth-0 select-list terminator (FROM / WHERE / set-op keyword / …).
function collectBranchAliases(tokens: readonly Token[], selectIdx: number): SelectListAlias[] {
  let endIdx = tokens.length;
  let depth = 0;
  for (let i = selectIdx + 1; i < tokens.length; i++) {
    const t = tokens[i]!;
    if (t.type === 'lparen') {
      depth++;
      continue;
    }
    if (t.type === 'rparen') {
      depth = Math.max(0, depth - 1);
      continue;
    }
    if (depth !== 0) continue;
    if (t.type === 'kw' && SELECT_LIST_TERMINATORS.has(t.text.toUpperCase())) {
      endIdx = i;
      break;
    }
  }

  const aliases: SelectListAlias[] = [];
  let itemStart = selectIdx + 1;
  let itemDepth = 0;
  for (let i = selectIdx + 1; i <= endIdx; i++) {
    const t = i < endIdx ? tokens[i]! : null;
    if (t) {
      if (t.type === 'lparen') {
        itemDepth++;
        continue;
      }
      if (t.type === 'rparen') {
        itemDepth = Math.max(0, itemDepth - 1);
        continue;
      }
      if (t.type !== 'comma' || itemDepth !== 0) continue;
    }
    const alias = findAliasInItem(tokens, itemStart, i);
    if (alias) aliases.push(alias);
    itemStart = i + 1;
  }
  return aliases;
}

function findAliasInItem(tokens: readonly Token[], from: number, to: number): SelectListAlias | null {
  let depth = 0;
  let lastAsIdx = -1;
  for (let i = from; i < to; i++) {
    const t = tokens[i]!;
    if (t.type === 'lparen') {
      depth++;
      continue;
    }
    if (t.type === 'rparen') {
      depth = Math.max(0, depth - 1);
      continue;
    }
    if (depth !== 0) continue;
    if (t.type === 'kw' && t.text.toUpperCase() === 'AS') {
      lastAsIdx = i;
    }
  }
  if (lastAsIdx === -1 || lastAsIdx + 1 >= to) return null;
  const tok = tokens[lastAsIdx + 1]!;
  if (tok.type !== 'ident' && tok.type !== 'kw') return null;
  const { content } = parseIdentifierToken(tok.text);
  if (content.length === 0) return null;
  return { name: content, start: tok.start, end: tok.end };
}

/**
 * Positions where a SELECT-list projection alias is a legal reference and
 * should surface as a completion candidate.
 *
 * HAVING / ORDER-BY are valid across all five engines; GROUP-BY is included
 * because MySQL, PostgreSQL, and SQLite accept select-list aliases there
 * (MSSQL / Oracle do not, but the resolver only consults this gate for the
 * engines that pass it).
 */
export function positionKindAcceptsSelectAlias(kind: CompletionPositionKind): boolean {
  return kind === 'having' || kind === 'order-by' || kind === 'group-by';
}

/**
 * Emit SELECT-list projection aliases as `'alias'`-kind candidates when the
 * cursor sits in a position that accepts them. No-op when the position
 * doesn't accept aliases or when the statement has no AS-bound projections.
 *
 * Engines pass a `quoteIdentifier` adapter so the `insertText` round-trips
 * through their identifier-quoting rules (mssql `[…]`, mysql `` `…` ``,
 * pgsql / oracle / sqlite `"…"`).
 */
export function emitSelectListAliasCandidates(
  out: CompletionCandidate[],
  source: string,
  statementRange: OffsetRange,
  cursor: number,
  engineId: DatabaseEngineId,
  positionKind: CompletionPositionKind,
  quoteIdentifier: (name: string) => string,
): void {
  if (!positionKindAcceptsSelectAlias(positionKind)) return;
  const aliases = extractSelectListAliases(source, statementRange, cursor, engineId);
  if (aliases.length === 0) return;
  const seen = new Set<string>();
  for (const a of aliases) {
    if (seen.has(a.name)) continue;
    seen.add(a.name);
    out.push({
      label: a.name,
      insertText: quoteIdentifier(a.name),
      isSnippet: false,
      kind: 'alias',
      tier: 1,
      detail: 'SELECT-list alias',
    });
  }
}
